import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CalendarCheck, Loader2, Package, MapPin } from "lucide-react";
import { WaBusinessLink, useBusinessContact } from "@/lib/whatsapp";

const fmtDay = (iso) => new Date(iso).toLocaleDateString(undefined, { weekday: "long", month: "short", day: "numeric" });
const fmtTime = (iso, tz) => new Date(iso).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit", timeZone: tz });

export default function PickupBooking() {
  const contact = useBusinessContact();
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [slot, setSlot] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/pickup/${token}`).then((r) => setData(r.data))
      .catch((e) => setError(apiError(e.response?.data?.detail) || "Pickup link not found"));
  }, [token]);

  const book = async () => {
    if (!slot) { toast.error("Pick a time first"); return; }
    setBusy(true);
    try { const { data: d } = await api.post(`/pickup/${token}`, { slot, note }); setData(d); toast.success("Pickup booked — see you then!"); }
    catch (e) { toast.error(apiError(e.response?.data?.detail) || "Could not book"); }
    finally { setBusy(false); }
  };

  if (error) return <div className="max-w-xl mx-auto px-4 py-16 text-center" data-testid="pickup-error"><p className="text-zinc-300">{error}</p><Link to="/" className="text-[#ff6a13] font-semibold mt-3 inline-block">Back home</Link></div>;
  if (!data) return <p className="text-zinc-500 font-mono-p max-w-xl mx-auto px-4 py-16">Loading…</p>;

  const byDay = data.slots.reduce((acc, s) => { const k = fmtDay(s); (acc[k] = acc[k] || []).push(s); return acc; }, {});
  const booked = data.pickup_slot;
  const locFor = (iso) => data.locations?.[iso.slice(0, 10)] || "";
  const slotLoc = slot ? locFor(slot) : booked ? (data.pickup_location || locFor(booked)) : "";

  return (
    <div className="max-w-2xl mx-auto px-4 py-12" data-testid="pickup-booking">
      <p className="text-xs uppercase tracking-wider text-zinc-500 font-mono-p">Order {data.order_number}</p>
      <h1 className="font-display text-4xl sm:text-5xl font-black text-white mt-1 flex items-center gap-3"><Package className="text-[#10b981]" size={36} />Ready for collection</h1>
      <p className="text-zinc-400 mt-2">Hi {data.customer_name || "there"} — pick a time to collect and we'll have everything ready.</p>
      <div className={`mt-4 rounded-xl border p-3 text-sm ${data.balance_owing > 0 ? "border-[#ff6a13]/40 bg-[#ff6a13]/10 text-[#ff6a13]" : "border-[#10b981]/40 bg-[#10b981]/10 text-[#10b981]"}`} data-testid="pickup-balance">
        {data.balance_owing > 0 ? <>Balance due before pickup: <strong className="font-mono-p">${data.balance_owing.toFixed(2)}</strong> — please pay by direct deposit before you collect.</> : "Paid in full — nothing due at pickup."}
      </div>
      {data.collected ? (
        <p className="mt-6 text-zinc-300" data-testid="pickup-collected">This order has already been collected — thank you!</p>
      ) : (
        <>
          {!booked && data.missed_pickup_slot && (
            <div className="mt-6 rounded-2xl border border-[#ff6a13]/50 bg-[#ff6a13]/10 p-4" data-testid="pickup-missed">
              <p className="text-white font-semibold">We missed you on {fmtDay(data.missed_pickup_slot)} at {fmtTime(data.missed_pickup_slot, data.tz)}</p>
              <p className="text-xs text-zinc-400 mt-1">No worries — pick a new time below and we'll have your order ready.</p>
            </div>
          )}
          {booked && (
            <div className="mt-6 rounded-2xl border border-[#10b981] bg-[#10b981]/10 p-4 flex items-start gap-3" data-testid="pickup-confirmed">
              <CalendarCheck className="text-[#10b981] shrink-0" />
              <div><p className="text-white font-semibold">Booked: {fmtDay(booked)} at {fmtTime(booked, data.tz)}</p>{data.pickup_location && <p className="text-xs text-[#10b981] mt-0.5 flex items-center gap-1" data-testid="pickup-confirmed-location"><MapPin size={12} />{data.pickup_location}</p>}<p className="text-xs text-zinc-400 mt-1">Need a different time? Pick another slot below and we'll update it.</p></div>
            </div>
          )}
          {data.closed?.length > 0 && (
            <p className="mt-4 text-xs text-[#ffd600] font-mono-p" data-testid="pickup-closed-notice">Closed: {data.closed.map((c) => `${new Date(c.date + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}${c.label ? ` (${c.label})` : ""}`).join(", ")}</p>
          )}
          <div className="mt-6 space-y-5">
            {Object.entries(byDay).map(([day, slots]) => (
              <div key={day}>
                <p className="text-sm font-semibold text-zinc-200 mb-2 flex items-center gap-2">{day}<span className="text-xs text-zinc-500 font-normal flex items-center gap-1" data-testid={`pickup-day-location-${slots[0].slice(0, 10)}`}><MapPin size={11} className="text-[#22c3d6]" />{locFor(slots[0])}</span></p>
                <div className="flex flex-wrap gap-2">
                  {slots.map((s) => (
                    <button key={s} onClick={() => setSlot(s)} data-testid={`pickup-slot-${s.slice(0, 13)}`} className={`h-9 rounded-full px-4 text-sm font-semibold border transition-colors ${slot === s ? "bg-[#10b981] border-[#10b981] text-black" : s === booked ? "border-[#10b981]/60 text-[#10b981]" : "border-zinc-700 text-zinc-300 hover:border-zinc-500"}`}>{fmtTime(s, data.tz)}</button>
                  ))}
                </div>
              </div>
            ))}
            {data.slots.length === 0 && <p className="text-zinc-500">No open slots in the next week — reply to your email or WhatsApp 441-704-6671 and we'll sort a time.</p>}
          </div>
          <Input value={note} onChange={(e) => setNote(e.target.value.slice(0, 200))} placeholder="Anything we should know? (optional)" data-testid="pickup-note" className="mt-6 bg-[#1e222d] border-zinc-700 text-white" />
          <Button onClick={book} disabled={busy || !slot} data-testid="pickup-book-btn" className="mt-4 w-full h-12 bg-[#10b981] hover:bg-[#34d399] text-black rounded-full font-bold text-base disabled:opacity-40">
            {busy ? <Loader2 className="animate-spin" /> : booked ? "Change my pickup time" : "Book this pickup time"}{slot && slotLoc ? ` · ${slotLoc}` : ""}
          </Button>
          <p className="text-xs text-zinc-500 mt-3 text-center">Times shown in Bermuda time. Something else? WhatsApp <WaBusinessLink className="text-[#22c3d6]" num={contact.whatsapp_number} text={`Hi, I'd like to arrange pickup for order ${data?.order_number || ""}.`} data-testid="pickup-whatsapp-link">{contact.whatsapp_display}</WaBusinessLink>.</p>
        </>
      )}
    </div>
  );
}
